import { useEffect, useState } from "react";
import { Youtube, ExternalLink, Play, Bell } from "lucide-react";
import { api } from "../lib/api";
import logger from "../lib/logger";
import { trackEventBeacon } from "../lib/analytics";
import FanQuestionForm from "../components/FanQuestionForm";

export default function YouTube() {
  const [config, setConfig] = useState(null);
  const [latestVideo, setLatestVideo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let configData = null;
        let videoData = null;

        try {
          configData = await api.getYouTubeConfig();
        } catch (err) {
          logger.warn("YouTube config fetch failed:", err.message);
        }

        try {
          videoData = await api.getLatestYouTubeVideo();
        } catch (err) {
          logger.warn("YouTube video fetch failed:", err.message);
        }

        setConfig(configData);
        setLatestVideo(videoData);
      } catch (err) {
        logger.warn("YouTube data fetch error:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-demolition-black flex items-center justify-center">
        <div className="animate-pulse text-demolition-red font-russo text-2xl">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="bg-demolition-black min-h-screen" data-testid="youtube-page">
      {/* Header */}
      <section className="relative py-24 px-6 md:px-12 overflow-hidden">
        <div className="absolute inset-0 steel-mesh opacity-20" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center">
            <Youtube className="w-16 h-16 text-demolition-red mx-auto mb-6" />
            <h1 
              className="font-russo text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tighter mb-4"
              data-testid="youtube-title"
            >
              DEMOPOD
            </h1>
            <p className="font-teko text-xl sm:text-2xl text-demolition-chrome uppercase tracking-widest">
              The Official Demolition YouTube Channel
            </p>
          </div>
        </div>
      </section>

      {/* Featured Video */}
      <section className="py-16 px-6 md:px-12 bg-demolition-paper" data-testid="featured-video-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-russo text-3xl text-white uppercase tracking-tighter mb-2">
              {["api", "rss", "cached", "hardcoded"].includes(latestVideo?.source) ? "LATEST VIDEO" : "FEATURED VIDEO"}
            </h2>
            {latestVideo?.title && ["api", "rss", "cached", "hardcoded"].includes(latestVideo.source) && (
              <p className="font-teko text-xl text-demolition-chrome uppercase tracking-widest mt-2">
                {latestVideo.title}
              </p>
            )}
            <p className="font-mono text-white/50 mt-2">
              Check out the latest content from DemoPod
            </p>
          </div>

          <div className="aspect-video bg-demolition-subtle border border-white/10 overflow-hidden relative">
            {latestVideo?.embed_url ? (
              <iframe
                src={`${latestVideo.embed_url}?playsinline=1&rel=0`}
                title={latestVideo.title || "DemoPod Featured Video"}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                data-testid="featured-youtube-embed"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-white/30">
                <Play className="w-20 h-20 mb-4" />
                <p className="font-mono">No featured video configured</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Channel Info */}
      <section className="py-24 px-6 md:px-12" data-testid="channel-info-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="border-l-4 border-demolition-red pl-6">
                <h2 className="font-russo text-4xl text-white uppercase tracking-tighter mb-4">
                  SUBSCRIBE TO DEMOPOD
                </h2>
                <p className="font-mono text-white/70 leading-relaxed">
                  DemoPod is the ultimate podcast and video channel for Demolition fans. 
                  Get exclusive content, behind-the-scenes stories, classic match breakdowns, 
                  and interviews with wrestling legends.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-demolition-paper border border-white/10">
                  <Play className="w-8 h-8 text-demolition-red" />
                  <div>
                    <h3 className="font-teko text-lg text-white uppercase">Classic Match Reviews</h3>
                    <p className="font-mono text-sm text-white/50">Relive the greatest Demolition matches</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-demolition-paper border border-white/10">
                  <Bell className="w-8 h-8 text-demolition-red" />
                  <div>
                    <h3 className="font-teko text-lg text-white uppercase">Exclusive Interviews</h3>
                    <p className="font-mono text-sm text-white/50">Hear from Ax, Smash, and wrestling legends</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <a
                  href={config?.channel_url || "https://www.youtube.com/@DemoPod"}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEventBeacon("youtube_subscribe_click", "/youtube", { source: "youtube_page" })}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-demolition-red text-white font-teko text-xl uppercase tracking-widest hover:bg-demolition-red-hover transition-colors btn-skew"
                  data-testid="subscribe-btn"
                >
                  <Youtube className="w-6 h-6" />
                  <span>SUBSCRIBE NOW</span>
                  <ExternalLink className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Channel Preview */}
            <div className="relative">
              <div className="bg-demolition-paper border border-white/10 p-8">
                <div className="flex items-center gap-6 mb-6">
                  <div className="w-20 h-20 bg-demolition-red flex items-center justify-center">
                    <Youtube className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h3 className="font-russo text-2xl text-white uppercase">
                      @{config?.channel_handle || "DemoPod"}
                    </h3>
                    <p className="font-mono text-white/50">YouTube Channel</p>
                  </div>
                </div>
                
                <div className="space-y-4 border-t border-white/10 pt-6">
                  <p className="font-mono text-white/70 text-sm">
                    Join the community of Demolition fans from around the world. 
                    Subscribe and hit the bell to never miss an upload!
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-demolition-subtle">
                      <span className="font-russo text-2xl text-demolition-red block">10+</span>
                      <span className="font-mono text-xs text-white/50">Episodes</span>
                    </div>
                    <div className="text-center p-4 bg-demolition-subtle">
                      <span className="font-russo text-2xl text-demolition-red block">4K+</span>
                      <span className="font-mono text-xs text-white/50">Subscribers</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative element */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 border-4 border-demolition-red bg-demolition-black flex items-center justify-center">
                <span className="font-russo text-3xl text-demolition-red">D</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 bg-demolition-paper border-y border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-russo text-3xl text-white uppercase tracking-tighter mb-4">
            DON'T MISS AN EPISODE
          </h2>
          <p className="font-mono text-white/60 mb-8">
            New content drops regularly. Subscribe to stay updated on all things Demolition.
          </p>
          <a
            href={config?.channel_url || "https://www.youtube.com/@DemoPod"}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEventBeacon("youtube_visit_channel", "/youtube", { source: "youtube_page_cta" })}
            className="inline-flex items-center gap-3 px-8 py-4 border-2 border-demolition-red text-demolition-red font-teko text-xl uppercase tracking-widest hover:bg-demolition-red hover:text-white transition-colors btn-skew"
            data-testid="channel-link-btn"
          >
            <span>VISIT CHANNEL</span>
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Fan Questions for DemoPod — shared with home page */}
      <FanQuestionForm />
    </div>
  );
}
